import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  FlexColumn,
  Flex,
  SvgIcon,
} from '@elementary/common-ui/components';
import { Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type Props = {
  dbtErrorsByDbSchemaKey: Record<string, string>;
  allProjectsFailed: boolean;
  totalProjects: number;
};

const ErrorRow = styled(FlexColumn)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray8};
  padding: 8px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const ErrorHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const ProjectName = styled(Typography)`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  color: ${({ theme }) => theme.palette.common.gray12};
`;

const ErrorMessage = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray11};
  white-space: pre-wrap;
  word-break: break-word;
  padding: 8px 0;
`;

const StyledSvgIcon = styled(SvgIcon)`
  padding: 4px;
  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.palette.common.gray11};
  }
`;

const ExpandableErrorRow = ({
  projectName,
  error,
}: {
  projectName: string;
  error: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <ErrorRow>
      <ErrorHeader onClick={() => setIsExpanded(!isExpanded)}>
        <ProjectName>{projectName}</ProjectName>
        <StyledSvgIcon width={16} height={16} fillColor="common.gray11">
          {isExpanded ? (
            <ExpandMoreIcon />
          ) : (
            <ExpandMoreIcon sx={{ transform: 'rotate(-180deg)' }} />
          )}
        </StyledSvgIcon>
      </ErrorHeader>
      <Collapse in={isExpanded}>
        <ErrorMessage>{error}</ErrorMessage>
      </Collapse>
    </ErrorRow>
  );
};

const ConnectionErrorAlert = ({
  dbtErrorsByDbSchemaKey,
  allProjectsFailed,
  totalProjects,
}: Props) => {
  const failedCount = Object.keys(dbtErrorsByDbSchemaKey).length;

  const getErrorMessage = () => {
    if (totalProjects === 1) {
      return 'Failed to test the connection';
    }
    if (allProjectsFailed || failedCount === totalProjects) {
      return 'Failed to test all connections';
    }
    return `Failed to test ${failedCount} of ${totalProjects} connections`;
  };

  return (
    <Alert severity="error" title={getErrorMessage()}>
      <FlexColumn sx={{ mt: 1, width: '100%' }}>
        {Object.entries(dbtErrorsByDbSchemaKey).map(([projectName, error]) => (
          <ExpandableErrorRow
            key={projectName}
            projectName={projectName}
            error={error as unknown as string}
          />
        ))}
      </FlexColumn>
    </Alert>
  );
};

export default ConnectionErrorAlert;
